// DTI calculator for multi-borrower (V2) loans, on top of dtiEngine.
// Hard-money loans have no DTI.
import React, { useEffect, useMemo, useRef, useState } from "react"
import { Button, Checkbox, Input, Modal, Segmented, Select, Tabs, Tag, Tooltip } from "antd"
import { ExportOutlined, ExpandAltOutlined } from "@ant-design/icons"
import * as com from "../../Common"
import EstimatedPayment from "./EstimatedPayment"
import { buildEstimatorInitialValues, estimatePayment, estimatorOverrides } from "./dtiPayment"
import { computeDTI, PROGRAM_LABELS, PROGRAMS, rowsForParticipant, sectionForRow } from "./dtiEngine"
import { initialSettings } from "./dtiLoan"
import { EXCLUSION_REASONS, exclusionReason, needsExclusionReason } from "./dtiExclusionReasons"
import "./DTICalculatorV2.css"

const money = (value) => `${value < 0 ? "−" : ""}$${com.commaize(Math.round(Math.abs(value)))}`
const percent = (value) => (value === null || value === undefined ? "—" : `${value}%`)

// Where a row's name tags come from (row.category from dtiEngine).
const OWNER_SOURCE = {
    employment: "income on their 1003 (1b-1d)",
    other: "income on their 1003 (1e)",
    rental: "owners of the property on the 1003 (3a)",
    property: "owners of the property on the 1003 (3a)",
    tradeline: "owners selected on this liability in the 1003 (2c)",
    obligation: "listed on their 1003 (2d)",
    rent: "their current housing on the 1003 (1a)",
    subject: "the loan as a whole",
}

const NON_OCCUPANT_TIP = "Answered No to \"Will you occupy the property as your primary residence?\" (1003 Declarations 5a). "
    + "Their income and debts still count in the total DTI; some programs also require the occupying borrowers "
    + "to qualify on their own (Occupant-only DTI on the left)."

const SECTIONS = [
    { key: "income", title: "Income" },
    { key: "housing", title: "Housing" },
    { key: "debts", title: "Debts and other properties" },
]

export default function DTICalculatorV2({
    open,
    application,
    legacyView,
    settings,
    forceExpand,
    onCancel,
    onDone,
    onSelectRate,
    toRates,
    changeStep,
}) {
    const [setup, setSetup] = useState(() => initialSettings(application, settings))
    const [activeTab, setActiveTab] = useState("all")
    const [paymentData, setPaymentData] = useState(null)
    const [minimized, setMinimized] = useState(false)
    // A new estimator for every visit: reopening before the close animation ends (or a
    // backgrounded tab, where it never ends) kept the previous visit's unsaved inputs.
    const [visit, setVisit] = useState(0)

    // Re-seed from the saved settings every time the calculator opens.
    useEffect(() => {
        if (!open) {
            // Unsaved estimator edits do not carry over to the next visit.
            setPaymentData(null)
            return
        }
        setSetup(initialSettings(application, settings))
        setVisit((count) => count + 1)
        setActiveTab("all")
        setMinimized(false)
    }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

    // Clicking DTI in the loan header while the calculator is minimized (after a 1003 link) brings it back.
    useEffect(() => {
        if (forceExpand) setMinimized(false)
    }, [forceExpand])

    const program = setup.program || setup.autoProgram
    // Loan type (upfront fee, MI) follows the guideline the LO picked.
    // estimatorBase: from the 1003 (and a rate picked in the rate selection) only — what
    // Reset all returns to and what the saved inputs are compared against.
    // estimatorInitial: the base with the inputs saved in the DTI settings laid over it.
    const estimatorBase = useMemo(
        () => buildEstimatorInitialValues(legacyView, com.getCustomerId(), program, null, { ignoreSaved: true }),
        [legacyView, program],
    )
    const estimatorInitial = useMemo(
        () => buildEstimatorInitialValues(legacyView, com.getCustomerId(), program, setup.estimator),
        [legacyView, program, setup.estimator],
    )
    const result = useMemo(() => computeDTI(application, {
        program,
        underwriting: setup.underwriting,
        excluded: setup.excluded,
        pendingSaleContract: setup.pendingSaleContract,
        payment: {
            total: paymentData ? paymentData.total : estimatePayment(estimatorInitial).total,
            breakdown: paymentData ? paymentData.breakdown : estimatePayment(estimatorInitial).breakdown,
            isEstimate: estimatorInitial.rateSource === "default"
                && Number(paymentData?.formValues?.interestRate ?? estimatorInitial.interestRate) === Number(estimatorInitial.interestRate),
        },
    }), [application, program, setup, paymentData, estimatorInitial])

    const toggle = (field, id, on) => setSetup((prev) => {
        const next = new Set(prev[field])
        if (on) next.add(id)
        else next.delete(id)
        return { ...prev, [field]: [...next] }
    })

    const goTo1003 = (row) => {
        const section = sectionForRow(row)
        if (!section || !changeStep) return
        setMinimized(true)
        const partyId = row.partyIds.length === 1 ? row.partyIds[0] : ""
        changeStep("1003View", partyId && partyId !== result.participants[0]?.partyId ? "coborrower" : "borrower", `#${section}`, partyId)
    }

    const handleDone = () => {
        // Every estimator input that differs from the 1003 is saved with the settings, so the
        // header, pricing and the next visit show the same payment. The 1003 is not changed.
        const values = paymentData?.formValues || estimatorInitial
        const changes = estimatorOverrides(estimatorBase, values)
        // Still exactly what the rate selection saved → keep that source (MI reads "from the product").
        const saved = setup.estimator || {}
        const untouched = saved.source === "rateSelection"
            && Object.keys(changes).every((key) => changes[key] === saved[key])
            && Object.keys(saved).every((key) => key === "source" || changes[key] === saved[key])
        const estimator = Object.keys(changes).length ? { ...changes, source: untouched ? "rateSelection" : "calculator" } : undefined
        onDone({
            settings: {
                program: setup.program,
                underwriting: setup.underwriting,
                excluded: setup.excluded,
                pendingSaleContract: setup.pendingSaleContract,
                // Only rows still left out keep a reason.
                exclusionReasons: Object.fromEntries(Object.entries(setup.exclusionReasons || {})
                    .filter(([id, reason]) => setup.excluded.includes(id) && reason?.code)),
                estimator,
            },
            // The older rate / interest-only columns are folded into settings.estimator.
            dtiRate: 0,
            interestOnly: false,
            result,
        })
    }

    const names = Object.fromEntries(result.participants.map((p) => [p.partyId, p]))
    const tabs = [
        { key: "all", label: "All" },
        ...result.participants.map((p) => ({
            key: p.partyId,
            label: (
                <span>
                    {p.name}
                    {!p.occupant && (
                        <Tooltip title={NON_OCCUPANT_TIP}>
                            <Tag className="dti-v2-tag-nonocc">Non-occupant</Tag>
                        </Tooltip>
                    )}
                </span>
            ),
        })),
    ]
    const coversEveryone = (row) => result.participants.length > 1
        && result.participants.every((p) => row.partyIds.includes(p.partyId))
    const ownerTip = (row) => {
        if (coversEveryone(row)) return "Belongs to the loan as a whole, so it counts for every borrower"
        const who = row.partyIds.map((id) => names[id]?.name).filter(Boolean).join(", ")
        return `${who}: ${OWNER_SOURCE[row.category] || "from the 1003"}`
    }
    const limits = result.limits || {}
    // occupantIncome is null only when every borrower occupies; 0 means the occupants have no income.
    const hasNonOccupant = result.totals.occupantIncome !== null

    const setReason = (id, patch) => setSetup((prev) => ({
        ...prev,
        exclusionReasons: { ...prev.exclusionReasons, [id]: { ...(prev.exclusionReasons || {})[id], ...patch } },
    }))
    const reasonFor = (id) => (setup.exclusionReasons || {})[id] || {}
    const missingReasons = ["debts", "housing"].reduce((count, section) => count
        + result[section].filter((row) => row.excluded && !reasonFor(row.id).code).length, 0)

    const renderReason = (row) => {
        const reason = reasonFor(row.id)
        const hint = exclusionReason(reason.code)?.doc
        return (
            <div className="dti-v2-row-reason">
                <Select
                    size="small"
                    className="dti-v2-reason-select"
                    placeholder="Why is it left out?"
                    status={reason.code ? "" : "warning"}
                    value={reason.code || undefined}
                    onChange={(code) => setReason(row.id, { code })}
                    options={EXCLUSION_REASONS.map(({ code, label }) => ({ value: code, label }))}
                    popupMatchSelectWidth={false}
                />
                {reason.code === "other" && (
                    <Input
                        size="small"
                        className="dti-v2-reason-note"
                        placeholder="Explain"
                        maxLength={500}
                        value={reason.note || ""}
                        onChange={(event) => setReason(row.id, { note: event.target.value })}
                    />
                )}
                {hint && reason.code !== "other" && <div className="dti-v2-row-note">Needs: {hint}</div>}
            </div>
        )
    }

    const renderRow = (row, section) => {
        const isPending = row.pendingSale && (program === "fnma" || program === "fhlmc")
        return (
            <div key={row.id} className={`dti-v2-row ${row.excluded ? "is-excluded" : ""}`}>
                <Checkbox
                    checked={!row.excluded}
                    onChange={(event) => toggle("excluded", row.id, !event.target.checked)}
                    aria-label={`Include ${row.label}`}
                />
                <div className="dti-v2-row-main">
                    <div className="dti-v2-row-title">
                        <span className="dti-v2-row-label">{row.label}</span>
                        <Tooltip title={ownerTip(row)}>
                            <span className="dti-v2-owners">
                                {coversEveryone(row)
                                    ? <Tag className="dti-v2-tag-owner">All borrowers</Tag>
                                    : row.partyIds.map((id) => (
                                        <Tag key={id} className="dti-v2-tag-owner">{names[id]?.shortName || "Borrower"}</Tag>
                                    ))}
                            </span>
                        </Tooltip>
                        {sectionForRow(row) && (
                            <Tooltip title={`Open 1003 section ${sectionForRow(row)}`}>
                                <ExportOutlined className="dti-v2-row-link" onClick={() => goTo1003(row)} />
                            </Tooltip>
                        )}
                    </div>
                    {row.note && <div className="dti-v2-row-note">{row.note}</div>}
                    {row.excluded && needsExclusionReason(section) && renderReason(row)}
                    {isPending && (
                        <Checkbox
                            className="dti-v2-row-contract"
                            checked={setup.pendingSaleContract.includes(row.propertyKey)}
                            onChange={(event) => toggle("pendingSaleContract", row.propertyKey, event.target.checked)}
                        >
                            Executed sale contract, financing contingencies cleared
                        </Checkbox>
                    )}
                </div>
                <div className="dti-v2-row-amount">{row.ruleExcluded && !row.monthly ? "Excluded" : money(row.monthly)}</div>
            </div>
        )
    }

    const sectionRows = (key) => {
        const rows = result[key]
        return activeTab === "all" ? rows : rowsForParticipant(rows, activeTab)
    }
    const sectionTotal = (rows) => rows.filter((row) => !row.excluded).reduce((sum, row) => sum + row.monthly, 0)

    const metric = ({ label, value, limit, emptyText, limitText, primary }) => (
        <div className={`dti-v2-metric ${primary ? "is-primary" : ""}`}>
            <div className="dti-v2-metric-label">{label}</div>
            <div className={`dti-v2-metric-value ${limit && value !== null && value > limit ? "is-over" : ""}`}>
                {value === null ? "—" : `${value}%`}
            </div>
            <div className="dti-v2-metric-limit">{value === null && emptyText ? emptyText : limitText}</div>
        </div>
    )

    if (open && minimized) {
        return (
            <div className="dti-v2-minimized">
                <span>DTI {percent(result.totals.backRatio)}</span>
                <Button size="small" icon={<ExpandAltOutlined />} onClick={() => setMinimized(false)}>Open calculator</Button>
            </div>
        )
    }

    return (
        <Modal
            open={open}
            width="90%"
            className="dti-v2-modal"
            styles={{ mask: { backgroundColor: "rgba(0, 0, 0, 0.75)" } }}
            maskClosable={false}
            title={null}
            onCancel={onCancel}
            footer={[
                <Button key="cancel" type="link" className="dti-v2-btn-cancel" onClick={onCancel}>Cancel</Button>,
                <Button key="done" type="primary" className="dti-v2-btn-done" onClick={handleDone}>Done</Button>,
            ]}
            destroyOnClose
        >
            <div className="dti-v2-top">DTI Calculator</div>
            <div className="dti-v2-layout">
                <aside className="dti-v2-summary">
                    <div className="dti-v2-settings">
                        <div className="dti-v2-field">
                            <div className="dti-v2-field-label">Guideline</div>
                            <Select
                                value={program}
                                className="dti-v2-select"
                                popupMatchSelectWidth={false}
                                onChange={(value) => setSetup((prev) => ({ ...prev, program: value === prev.autoProgram ? "" : value }))}
                                options={PROGRAMS.map((value) => ({ value, label: PROGRAM_LABELS[value] }))}
                            />
                            <div className="dti-v2-field-hint">
                                {setup.program
                                    ? <>Changed from {PROGRAM_LABELS[setup.autoProgram]} · <span className="dti-v2-link" onClick={() => setSetup((prev) => ({ ...prev, program: "" }))}>Reset</span></>
                                    : "Set from the loan type"}
                            </div>
                        </div>
                        <div className="dti-v2-field">
                            <div className="dti-v2-field-label">Underwriting</div>
                            <Segmented
                                block
                                value={setup.underwriting}
                                onChange={(value) => setSetup((prev) => ({ ...prev, underwriting: value }))}
                                options={[{ value: "manual", label: "Manual" }, { value: "aus", label: "DU / LPA" }]}
                            />
                        </div>
                    </div>
                    {metric({
                        label: "Total DTI",
                        primary: true,
                        value: result.totals.backRatio,
                        limit: limits.back,
                        limitText: limits.back ? `Limit ${limits.back}%` : "Limit per AUS findings",
                    })}
                    <div className="dti-v2-metric-pair">
                        {metric({
                            label: "Housing ratio",
                            value: result.totals.frontRatio,
                            limit: limits.front,
                            limitText: limits.front ? `Limit ${limits.front}%` : "No guide limit",
                        })}
                        {hasNonOccupant && metric({
                            label: "Occupant-only DTI",
                            value: result.totals.occupantBackRatio,
                            limit: limits.occupantBack,
                            emptyText: "No occupant income",
                            limitText: limits.occupantBack ? `Limit ${limits.occupantBack}%` : "No guide limit",
                        })}
                    </div>
                    {(result.warnings.length > 0 || missingReasons > 0) && (
                        <ul className="dti-v2-warnings">
                            {missingReasons > 0 && (
                                <li key="missingReasons">
                                    {missingReasons === 1 ? "1 left-out item has" : `${missingReasons} left-out items have`} no reason yet
                                </li>
                            )}
                            {result.warnings.map((warning) => (
                                <li key={warning.code}>
                                    {warning.text}
                                    {warning.code === "estimatedPayment" && onSelectRate && (
                                        <span className="dti-v2-link" onClick={onSelectRate}> Select rate</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </aside>

                <section className="dti-v2-detail">
                    <div className="dti-v2-estimator">
                        <EstimatedPayment
                            key={visit}
                            application={legacyView}
                            initialValues={estimatorInitial}
                            baseValues={estimatorBase}
                            restoreValues={paymentData?.formValues}
                            onPaymentDataChange={setPaymentData}
                            toRates={() => {
                                setMinimized(true)
                                if (toRates) toRates()
                            }}
                            onSelectRate={onSelectRate}
                        />
                    </div>

                    <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabs} />
                    <div className="dti-v2-tab-hint">
                        {activeTab === "all"
                            ? "Name tags show whose 1003 each row comes from; hover a tag for the source."
                            : "Rows this borrower owns or shares. The ratios on the left are always for the whole loan."}
                    </div>

                    {SECTIONS.map(({ key, title }) => {
                        const rows = sectionRows(key)
                        if (!rows.length) return null
                        return (
                            <div key={key} className="dti-v2-section">
                                <div className="dti-v2-section-head">
                                    <span>{title}</span>
                                    <span>{money(sectionTotal(rows))}</span>
                                </div>
                                {rows.map((row) => renderRow(row, key))}
                            </div>
                        )
                    })}
                </section>
            </div>
        </Modal>
    )
}
